import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { writeRegistrationDraft, readRegistrationDraft, registrationOwner,
    REGISTRATION_DRAFT_EVENT, REGISTRATION_DRAFT_KEY } from '../utils/registrationDraft';
import { clearPermissionDraft, permissionCheckpointEvent, savePermissionDraft } from '../utils/permissionDraft';

export default function useRegistrationDraftCheckpoint(initial, path, payload, journey) {
    const lease = useRef(registrationOwner());
    const expected = useRef(initial || null);
    const latest = useRef(null);
    const writing = useRef(false);
    const stopped = useRef(initial?.status === 'pending');
    const [saved, setSaved] = useState(Boolean(initial));
    const [failed, setFailed] = useState(false);
    const [conflict, setConflict] = useState(false);
    useLayoutEffect(() => { latest.current = { path, payload }; });
    const flush = () => {
        if (stopped.current) return { ok: false };
        writing.current = true;
        const { path: currentPath, payload: currentPayload } = latest.current;
        const values = typeof currentPayload === 'function' ? currentPayload() : currentPayload;
        const result = writeRegistrationDraft(expected.current, values, { path: currentPath, flowId: journey.id, source: journey.source }, lease.current);
        writing.current = false;
        if (result.ok) { expected.current = result.draft; setSaved(Boolean(result.draft)); setFailed(false); }
        else if (result.conflict) { stopped.current = true; setConflict(true); }
        else setFailed(true);
        return result;
    };
    const flushRef = useRef(flush);
    const lastStage = useRef(null);
    useLayoutEffect(() => { flushRef.current = flush; });
    useLayoutEffect(() => {
        const values = typeof payload === 'function' ? payload() : payload;
        const stage = `${values.entryMode}:${values.manualStep}`;
        if (lastStage.current !== null && lastStage.current !== stage) flushRef.current();
        lastStage.current = stage;
    });
    useEffect(() => {
        const timer = setTimeout(() => flushRef.current(), 400);
        return () => clearTimeout(timer);
    });
    useEffect(() => {
        const save = () => flushRef.current();
        const hidden = () => { if (document.visibilityState === 'hidden') save(); };
        const permission = () => {
            const result = save();
            if (result.ok && result.draft) savePermissionDraft(lease.current.uuid, latest.current.path,
                { ...result.draft.input, registrationContext: lease.current.context });
        };
        const invalidated = () => { stopped.current = true; setConflict(true); clearPermissionDraft(); };
        const check = () => {
            if (writing.current || stopped.current) return;
            const current = readRegistrationDraft();
            if ((current?.id || null) !== (expected.current?.id || null) || (current?.revision || 0) !== (expected.current?.revision || 0)
                || current?.writeId !== expected.current?.writeId) invalidated();
        };
        const storage = e => { if (e.key === REGISTRATION_DRAFT_KEY || e.key === null) check(); };
        window.addEventListener('pagehide', save);
        document.addEventListener('visibilitychange', hidden);
        window.addEventListener(permissionCheckpointEvent, permission);
        window.addEventListener('kkomok-account-context-changed', invalidated);
        window.addEventListener(REGISTRATION_DRAFT_EVENT, check);
        window.addEventListener('storage', storage);
        return () => {
            window.removeEventListener('pagehide', save);
            document.removeEventListener('visibilitychange', hidden);
            window.removeEventListener(permissionCheckpointEvent, permission);
            window.removeEventListener('kkomok-account-context-changed', invalidated);
            window.removeEventListener(REGISTRATION_DRAFT_EVENT, check);
            window.removeEventListener('storage', storage);
            if (!stopped.current) save();
            clearPermissionDraft();
        };
    }, []);
    return { saved, failed, conflict, flush, expected, lease,
        pause: () => { stopped.current = true; clearPermissionDraft(); },
        resume: draft => { expected.current = draft; stopped.current = false; } };
}
